import { useCallback, useRef, useState, useMemo } from "react";
import * as PR from "../../prime-modules/index";
import { QRCodeCanvas } from "qrcode.react";
import {
  networkInfo,
  refundNewAPI,
  retryOrderStatus,
  updateShippingStatus,
} from "../../services/api";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik, FormikConsumer } from "formik";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";

const SHIPPING_STATUS_FLOW = [
  "SHIPPING_IN_PROGRESS",
  "SHIPPED",
  "IN_TRANSIT",
  "OUT_FOR_DELIVERY",
  "DELIVERY_ATTEMPTED",
  "DELIVERED",
];

const SHIPPING_UPDATE_ALLOWED_STATUSES = SHIPPING_STATUS_FLOW.slice(0, -1);

const shippingStatusOptions = SHIPPING_STATUS_FLOW.map((status) => ({
  label: status.replaceAll("_", " "),
  value: status,
}));

const OrderActionMenu = ({
  orders,
  refreshData,
  toast,
  showRecheckEsimStatus = true,
  displaySendEmailHandler,
}) => {
  const dispatch = useDispatch();
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const headers = useMemo(() => {
    return { sessionid: adminData.sessionId };
  }, [adminData.sessionId]);
  const menuRef = useRef(null);
  const appName = process.env?.REACT_APP_NAME;
  const [networkInfoLoading, setNetworkInfoLoading] = useState(true);
  const [fraudLoading, setFraudLoading] = useState(false);

  const [networkDetails, setNetworkDetails] = useState({});

  const isPhysicalSim = useMemo(
    () => orders?.dataPlans?.some(({ simtype }) => simtype === "pSIM"),
    [orders?.dataPlans],
  );
  const requiresIccid = !orders?.iccid?.trim?.();

  // Dialog State
  const [dialogState, setDialogState] = useState({
    orderInfoVisible: false,
    otherInfoVisible: false,
    qrCodeVisible: false,
    fraudVisible: false,
    refundAndRevokeVisible: false,
    cancelSubscriptionVisible: false,
    refundVisible: false,
    revokeBundleVisible: false,
    shippingStatusVisible: false,
  });

  // Validations
  const reasonValidation = Yup.string()
    .required("Reason is required")
    .min(3, "Reason must be at least 3 characters")
    .max(1000, "Reason must be at most 1000 characters");

  const validationSchemaCancelSubscription = Yup.object().shape({
    reason: reasonValidation,
    cancelType: Yup.string().required("Please select cancellation type"),
    revokeBundle: Yup.boolean(),
    refundPayment: Yup.boolean(),
  });

  const validationSchema = Yup.object().shape({
    reason: reasonValidation,
  });

  const validationSchemaShippingStatus = Yup.object().shape({
    status: Yup.string()
      .required("Shipping status is required")
      .oneOf(
        shippingStatusOptions.map((opt) => opt.value),
        "Shipping status is required",
      ),
    iccid: requiresIccid
      ? Yup.string().trim().required("ICCID is required")
      : Yup.string(),
    note: Yup.string().max(1000, "Note must be at most 1000 characters"),
  });

  // Helper Functions
  const getNetworkInfo = useCallback(() => {
    setNetworkInfoLoading(true);
    const networkDetailsResponse = (response) => {
      if (response.result === "SUCCESS") {
        if (response.data) {
          setNetworkDetails(response.data);
        } else {
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "No Network Details available",
          });
        }
      } else {
        const error = response.error;
        toast.current.show({
          severity: error.severity,
          summary: "Error",
          detail: error.errorMsg ? error.errorMsg : error.summary,
        });
      }
      setNetworkInfoLoading(false);
    };

    networkInfo(dispatch, headers, orders.iccid, networkDetailsResponse);
  }, [dispatch, headers, orders.iccid, toast]);

  const showDialog = useCallback(
    (type) => {
      if (type === "otherInfo") {
        getNetworkInfo();
      }
      setDialogState((prevState) => ({
        ...prevState,
        [`${type}Visible`]: true,
      }));
    },
    [getNetworkInfo]
  );

  const hideDialog = useCallback((type) => {
    setDialogState((prevState) => ({
      ...prevState,
      [`${type}Visible`]: false,
    }));
  }, []);

  const canUpdateShippingStatus =
    isPhysicalSim &&
    SHIPPING_UPDATE_ALLOWED_STATUSES.includes(orders?.orderStatus);
  // Menu Items
  const menuItems = [
    {
      label: "Mark as Fraudulent",
      command: () => showDialog("fraud"),
      enabled: hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS),
    },
    {
      label: "Revoke and Refund",
      command: () => showDialog("refundAndRevoke"),
      disabled: !orders?.isRefundable || !orders?.isRevocable,
      enabled: hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS),
    },
    {
      label: "Cancel Subscription",
      command: () => showDialog("cancelSubscription"),
      disabled:
        orders?.subscriptionStatus === "INACTIVE" ||
        orders?.subscriptionStatus === "CANCELLING" || !orders?.isSubscription,
      enabled: hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS),
    },
    {
      label: "Refund Payment",
      command: () => showDialog("refund"),
      disabled: !orders?.isRefundable,
      enabled: hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS),
    },
    {
      label: "Revoke Bundle",
      command: () => showDialog("revokeBundle"),
      disabled: !orders?.isRevocable,
      enabled: hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS),
    },
    {
      label: "QR Code",
      command: () => showDialog("qrCode"),
      disabled: !orders?.iccid,
      enabled: appName !== "data2go-iot",
    },
    {
      label: "Network Info",
      command: () => showDialog("otherInfo"),
      disabled: !orders?.iccid,
      enabled: appName !== "data2go-iot",
    },
    {
      label: "Update Shipping Status",
      command: () => showDialog("shippingStatus"),
      enabled: canUpdateShippingStatus,
    },
    {
      label: "Send Email",
      command: () => displaySendEmailHandler(),
      enabled: displaySendEmailHandler && hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS),
    },
    {
      label: "Recheck Order Status",
      command: () => handleRetryOrderAPI(orders?.orderId),
      disabled: !(orders?.orderStatus === "PAYMENT_FAILED" || orders?.orderStatus === "PURCHASE_FAILED"),
      enabled: showRecheckEsimStatus && hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS),
    },
  ].filter((m) => m.enabled).sort((a, b) => a.label.localeCompare(b.label));

  const availableShippingStatusOptions = useMemo(() => {
    const currentIndex = SHIPPING_STATUS_FLOW.indexOf(orders?.orderStatus);

    return currentIndex === -1
      ? []
      : shippingStatusOptions.filter(
        ({ value }) => SHIPPING_STATUS_FLOW.indexOf(value) > currentIndex,
      );
  }, [orders?.orderStatus]);

  // Handlers
  const shippingStatusSubmitHandler = (values, { resetForm }) => {
    setFraudLoading(true);
    const requestPayload = {
      status: values.status,
    };
    if (values.note && values.note.trim()) {
      requestPayload.note = values.note.trim();
    }
    if (requiresIccid) {
      requestPayload.iccid = values.iccid.trim();
    }

    const response = (res) => {
      setFraudLoading(false);
      hideDialog("shippingStatus");
      if (res.result === "SUCCESS") {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Shipping status updated successfully.",
        });
        refreshData && refreshData();
        resetForm();
      } else if (res.result === "FAILED" && res.error) {
        const error = res.error;
        toast.current?.show({
          severity: error.severity,
          summary: "Error",
          detail: error.errorMsg ? error.errorMsg : error.summary,
        });
      }
    };
    updateShippingStatus(
      orders.orderId,
      requestPayload,
      headers,
      dispatch,
      response,
    );
  };

  const handleRetryOrderAPI = (orderId) => {
    const saveNotesResponse = (response) => {
      if (response.result === "SUCCESS") {
        refreshData && refreshData();
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: `Recheck Status was successfully processed.`,
        });
      } else if (response.result === "FAILED" && response.error) {
        const error = response.error;
        toast.current?.show({
          severity: error.severity,
          summary: "Error",
          detail: error.errorMsg ? error.errorMsg : error.summary,
        });
      }
    };
    retryOrderStatus(dispatch, headers, orderId, saveNotesResponse);
  };
  const fraudulentSubmitHandler = (values, { resetForm }) => {
    const { reason } = values;
    if (reason.trim()) {
      setFraudLoading(true);
      const request = {
        fraud: true,
        refund: true,
        revoke: true,
        reason,
      };
      const response = (response) => {
        setFraudLoading(false);
        hideDialog("fraud");
        if (response.result === "SUCCESS") {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Fraudulent activity has been reported successfully.",
          });
          refreshData && refreshData();
          resetForm();
        } else if (response.result === "FAILED" && response.error) {
          const error = response.error;
          toast.current?.show({
            severity: error.severity,
            summary: "Error",
            detail: error.errorMsg ? error.errorMsg : error.summary,
          });
        }
      };
      refundNewAPI(request, dispatch, headers, orders.orderId, response);
    }
  };

  const refundSubmitHandler = (values, { resetForm }) => {
    const { reason } = values;
    if (reason.trim()) {
      setFraudLoading(true);
      const request = {
        fraud: false,
        refund: true,
        revoke: false,
        reason,
      };
      const response = (response) => {
        setFraudLoading(false);
        hideDialog("refund");
        if (response.result === "SUCCESS") {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Refund has been successfully processed.",
          });
          refreshData && refreshData();
          resetForm();
        } else if (response.result === "FAILED" && response.error) {
          const error = response.error;
          toast.current?.show({
            severity: error.severity,
            summary: "Error",
            detail: error.errorMsg ? error.errorMsg : error.summary,
          });
        }
      };
      refundNewAPI(request, dispatch, headers, orders.orderId, response);
    }
  };

  const refundAndRevokeSubmitHandler = (values, { resetForm }) => {
    const { reason } = values;
    if (reason.trim()) {
      setFraudLoading(true);
      const request = {
        fraud: false,
        refund: true,
        revoke: true,
        reason,
      };
      const response = (response) => {
        setFraudLoading(false);
        hideDialog("refundAndRevoke");
        if (response.result === "SUCCESS") {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Revoke and Refund has been successfully processed.",
          });
          refreshData && refreshData();
          resetForm();
        } else if (response.result === "FAILED" && response.error) {
          const error = response.error;
          toast.current?.show({
            severity: error.severity,
            summary: "Error",
            detail: error.errorMsg ? error.errorMsg : error.summary,
          });
        }
      };
      refundNewAPI(request, dispatch, headers, orders.orderId, response);
    }
  };

  const cancelSubscriptionSubmitHandler = (values, { resetForm }) => {
    if (values?.reason.trim()) {
      setFraudLoading(true);
      const request = {
        fraud: false,
        refund:
          values?.cancelType === "CancelAtThePeriodEnd"
            ? false
            : values?.refundPayment,
        revoke:
          values?.cancelType === "CancelAtThePeriodEnd"
            ? false
            : values?.revokeBundle,
        reason: values?.reason,
        cancelSubscription: values?.cancelType,
      };
      const response = (response) => {
        setFraudLoading(false);
        hideDialog("cancelSubscription");
        if (response.result === "SUCCESS") {
          let detailMessage =
            values?.cancelType === "CancelImmediately"
              ? "Subscription has been cancelled successfully."
              : "Subscription will be cancelled at the end of the current period.";

          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: detailMessage,
          });
          refreshData && refreshData();
          resetForm();
        } else if (response.result === "FAILED" && response.error) {
          const error = response.error;
          toast.current?.show({
            severity: error.severity,
            summary: "Error",
            detail: error.errorMsg ? error.errorMsg : error.summary,
          });
        }
      };
      refundNewAPI(request, dispatch, headers, orders.orderId, response);
    }
  };

  const revokeBundleSubmitHandler = (values, { resetForm }) => {
    const { reason } = values;
    if (reason.trim()) {
      setFraudLoading(true);
      const request = {
        fraud: false,
        refund: false,
        revoke: true,
        reason,
      };
      const response = (response) => {
        setFraudLoading(false);
        hideDialog("revokeBundle");
        if (response.result === "SUCCESS") {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Revoke Bundle has been successfully processed..",
          });
          refreshData && refreshData();
          resetForm();
        } else if (response.result === "FAILED" && response.error) {
          const error = response.error;
          toast.current?.show({
            severity: error.severity,
            summary: "Error",
            detail: "Bundle revoke failed.",
          });
          refreshData && refreshData(); // Logic from original code also refreshed on error here? Original code had setRefresh(!refresh)
        }
      };
      refundNewAPI(request, dispatch, headers, orders.orderId, response);
    }
  };

  return (
    <div className="flex gap-2 justify-content-between1">
      <PR.Button
        icon="pi pi-ellipsis-v"
        className="icon-button"
        onClick={e => menuRef.current.toggle(e)}
      />
      <PR.Menu model={menuItems} popup ref={menuRef} />

      {/* Dialogs */}
      <PR.Dialog
        header="QR Code"
        visible={dialogState.qrCodeVisible}
        draggable={false}
        resizable={false}
        onHide={() => hideDialog("qrCode")}
        dismissableMask={true}
        className="p-dialog-centered information-dialog qrcode-dialog refund-payment-dialog"
      >
        <div className="qr-code">
          <QRCodeCanvas value={orders.qrCode} size={256} />
        </div>
      </PR.Dialog>
      <PR.Dialog
        header="Mark as Fraudulent"
        visible={dialogState.fraudVisible}
        onHide={() => hideDialog("fraud")}
        draggable={false}
        resizable={false}
        dismissableMask={true}
        modal
        className="p-dialog-centered information-dialog refund-payment-dialog"
      >
        {fraudLoading ? (
          <div
            className="flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <PR.ProgressSpinner animationDuration=".5s" />
          </div>
        ) : (
          <Formik
            initialValues={{ reason: "" }}
            validationSchema={validationSchema}
            onSubmit={fraudulentSubmitHandler}
          >
            {({ resetForm }) => (
              <Form className="p-mb-2 add-notes mb-3">
                <div>
                  <Field
                    name="reason"
                    as={PR.InputTextarea}
                    placeholder="Enter your reason"
                    className="p-mr-2"
                    autoResize
                    rows={4}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      maxHeight: "300px",
                    }}
                  />
                  <ErrorMessage
                    name="reason"
                    component="div"
                    className="p-error text-sm"
                  />
                </div>
                <div className="p-mt-2 text-right mt-2">
                  <PR.Button
                    type="submit"
                    label="Save"
                    className="p-button save-button"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </PR.Dialog>
      <PR.Dialog
        header="Refund Payment"
        visible={dialogState.refundVisible}
        onHide={() => hideDialog("refund")}
        draggable={false}
        resizable={false}
        dismissableMask={true}
        modal
        className="p-dialog-centered information-dialog refund-payment-dialog"
      >
        {fraudLoading ? (
          <div
            className="flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <PR.ProgressSpinner animationDuration=".5s" />
          </div>
        ) : (
          <Formik
            initialValues={{ reason: "" }}
            validationSchema={validationSchema}
            onSubmit={refundSubmitHandler}
          >
            {({ resetForm }) => (
              <Form className="p-mb-2 add-notes mb-3">
                <div>
                  <Field
                    name="reason"
                    as={PR.InputTextarea}
                    placeholder="Enter your reason"
                    className="p-mr-2"
                    autoResize
                    rows={4}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      maxHeight: "300px",
                    }}
                  />
                  <ErrorMessage
                    name="reason"
                    component="div"
                    className="p-error text-sm"
                  />
                </div>
                <div className="p-mt-2 text-right mt-2">
                  <PR.Button
                    type="submit"
                    label="Save"
                    className="p-button save-button"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </PR.Dialog>
      <PR.Dialog
        header="Revoke and Refund"
        visible={dialogState.refundAndRevokeVisible}
        onHide={() => hideDialog("refundAndRevoke")}
        draggable={false}
        resizable={false}
        dismissableMask={true}
        modal
        className="p-dialog-centered information-dialog refund-payment-dialog"
      >
        {fraudLoading ? (
          <div
            className="flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <PR.ProgressSpinner animationDuration=".5s" />
          </div>
        ) : (
          <Formik
            initialValues={{ reason: "" }}
            validationSchema={validationSchema}
            onSubmit={refundAndRevokeSubmitHandler}
          >
            {({ resetForm }) => (
              <Form className="p-mb-2 add-notes mb-3">
                <div>
                  <Field
                    name="reason"
                    as={PR.InputTextarea}
                    placeholder="Enter your reason"
                    className="p-mr-2"
                    autoResize
                    rows={4}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      maxHeight: "300px",
                    }}
                  />
                  <ErrorMessage
                    name="reason"
                    component="div"
                    className="p-error text-sm"
                  />
                </div>
                <div className="p-mt-2 text-right mt-2">
                  <PR.Button
                    type="submit"
                    label="Save"
                    className="p-button save-button"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </PR.Dialog>
      <PR.Dialog
        header="Cancel Subscription"
        visible={dialogState.cancelSubscriptionVisible}
        onHide={() => hideDialog("cancelSubscription")}
        draggable={false}
        resizable={false}
        dismissableMask={true}
        modal
        className="p-dialog-centered information-dialog refund-payment-dialog"
      >
        {fraudLoading ? (
          <div
            className="flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <PR.ProgressSpinner animationDuration=".5s" />
          </div>
        ) : (
          <Formik
            initialValues={{
              reason: "",
              cancelType: "",
              revokeBundle: false,
              refundPayment: false,
            }}
            validationSchema={validationSchemaCancelSubscription}
            onSubmit={cancelSubscriptionSubmitHandler}
          >
            {({ resetForm }) => (
              <Form className="p-mb-2 add-notes mb-3">
                {/* Radio Buttons Section */}
                <div className="p-mt-3">
                  <div className="flex gap-4">
                    <div className="flex align-items-center">
                      <Field
                        type="radio"
                        name="cancelType"
                        value="CancelImmediately"
                        as={PR.RadioButton}
                        inputId="cancelImmediately"
                      />
                      <label htmlFor="cancelImmediately" className="ml-2">
                        Cancel Immediately
                      </label>
                    </div>

                    <div className="flex align-items-center">
                      <Field
                        type="radio"
                        name="cancelType"
                        value="CancelAtThePeriodEnd"
                        as={PR.RadioButton}
                        inputId="cancelAtThePeriodEnd"
                      />
                      <label htmlFor="cancelAtThePeriodEnd" className="ml-2">
                        Cancel At The Period End
                      </label>
                    </div>
                  </div>
                  <ErrorMessage
                    name="cancelType"
                    component="div"
                    className="p-error text-sm mt-1"
                  />
                </div>

                {/* ✅ Conditionally render extra checkboxes when CancelImmediately is selected */}
                <FormikConsumer>
                  {({ values }) =>
                    values.cancelType === "CancelImmediately" && (
                      <div className="p-mt-3">
                        <br></br>

                        <div className="flex flex-col gap-2">
                          <div className="flex align-items-center">
                            <Field
                              type="checkbox"
                              name="revokeBundle"
                              as={PR.Checkbox}
                              inputId="revokeBundle"
                            />
                            <label htmlFor="revokeBundle" className="ml-2">
                              Revoke Bundle
                            </label>
                          </div>

                          <div className="flex align-items-center">
                            <Field
                              type="checkbox"
                              name="refundPayment"
                              as={PR.Checkbox}
                              inputId="refundPayment"
                            />
                            <label htmlFor="refundPayment" className="ml-2">
                              Refund Payment
                            </label>
                          </div>
                        </div>
                      </div>
                    )
                  }
                </FormikConsumer>

                <br />

                <div className="p-mt-4">
                  <Field
                    name="reason"
                    as={PR.InputTextarea}
                    placeholder="Enter your reason"
                    className="p-mr-2"
                    autoResize
                    rows={4}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      maxHeight: "300px",
                    }}
                  />
                  <ErrorMessage
                    name="reason"
                    component="div"
                    className="p-error text-sm"
                  />
                </div>

                <div className="p-mt-2 text-right mt-3">
                  <PR.Button
                    type="submit"
                    label="Save"
                    className="p-button save-button"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </PR.Dialog>
      <PR.Dialog
        header="Revoke Bundle"
        visible={dialogState.revokeBundleVisible}
        onHide={() => hideDialog("revokeBundle")}
        draggable={false}
        resizable={false}
        dismissableMask={true}
        modal
        className="p-dialog-centered information-dialog refund-payment-dialog"
      >
        {fraudLoading ? (
          <div
            className="flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <PR.ProgressSpinner animationDuration=".5s" />
          </div>
        ) : (
          <Formik
            initialValues={{ reason: "" }}
            validationSchema={validationSchema}
            onSubmit={revokeBundleSubmitHandler}
          >
            {({ resetForm }) => (
              <Form className="p-mb-2 add-notes mb-3">
                <div>
                  <Field
                    name="reason"
                    as={PR.InputTextarea}
                    placeholder="Enter your reason"
                    className="p-mr-2"
                    autoResize
                    rows={4}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      maxHeight: "300px",
                    }}
                  />
                  <ErrorMessage
                    name="reason"
                    component="div"
                    className="p-error text-sm"
                  />
                </div>
                <div className="p-mt-2 text-right mt-2">
                  <PR.Button
                    type="submit"
                    label="Save"
                    className="p-button save-button"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </PR.Dialog>
      <PR.Dialog
        header="Network Information"
        visible={dialogState.otherInfoVisible}
        onHide={() => hideDialog("otherInfo")}
        draggable={false}
        resizable={false}
        dismissableMask={true}
        modal
        className="p-dialog-centered information-dialog refund-payment-dialog"
      >
        {networkInfoLoading ? (
          <div
            className="flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <PR.ProgressSpinner animationDuration=".5s" />
          </div>
        ) : networkDetails &&
          (networkDetails?.mobileNetworkCode ||
            networkDetails?.networkName ||
            networkDetails?.country ||
            networkDetails?.lastSeen) ? (
          <div className="info-block">
            <ul className="flex align-items-center list-none p-0">
              <li>
                Mobile Network Code <span>:</span>
              </li>
              <li>{networkDetails?.mobileNetworkCode}</li>
            </ul>
            <ul className="flex align-items-center list-none p-0">
              <li>
                Network Name <span>:</span>
              </li>
              <li>{networkDetails?.networkName}</li>
            </ul>
            <ul className="flex align-items-center list-none p-0">
              <li>
                Country <span>:</span>
              </li>
              <li>{networkDetails?.country}</li>
            </ul>
            <ul className="flex align-items-center list-none p-0">
              <li>
                Last seen <span>:</span>
              </li>
              <li>{networkDetails?.lastSeen}</li>
            </ul>
          </div>
        ) : (
          <div
            className="flex justify-content-center align-items-center"
            style={{ minHeight: "100px" }}
          >
            <span>No Information Available</span>
          </div>
        )}
      </PR.Dialog>
      <PR.Dialog
        header="Update Shipping Status"
        visible={dialogState.shippingStatusVisible}
        onHide={() => !fraudLoading && hideDialog("shippingStatus")}
        draggable={false}
        resizable={false}
        dismissableMask={!fraudLoading}
        modal
        className="p-dialog-centered information-dialog refund-payment-dialog"
        style={{ width: "450px" }}
      >
        <Formik
          initialValues={{
            status: "",
            iccid: orders?.iccid || "",
            note: "",
          }}
          validationSchema={validationSchemaShippingStatus}
          onSubmit={shippingStatusSubmitHandler}
        >
          {({ values, handleBlur, handleChange, setFieldValue }) => (
            <Form className="p-mb-2 add-notes mb-3">
              <div className="mb-3 flex align-items-center">
                <label className="font-bold text-sm mr-2">
                  Current Status:
                </label>

                <span className="text-sm">
                  {shippingStatusOptions.find(
                    (status) => status.value === orders?.orderStatus,
                  )?.label || orders?.orderStatus}
                </span>
              </div>
              <div className="mb-3">
                <label className="block mb-1 font-bold text-sm">
                  Status <span style={{ color: "red" }}>*</span>
                </label>
                <PR.Dropdown
                  id="status"
                  name="status"
                  value={values.status}
                  options={availableShippingStatusOptions}
                  onChange={(e) => setFieldValue("status", e.value)}
                  placeholder="Select Status"
                  className="w-full"
                  style={{ width: "100%" }}
                  disabled={
                    fraudLoading || availableShippingStatusOptions.length === 0
                  }
                />
                <ErrorMessage
                  name="status"
                  component="div"
                  className="p-error text-sm mt-1"
                />
              </div>

              {requiresIccid && (
                <div className="mb-3">
                  <label className="block mb-1 font-bold text-sm" htmlFor="iccid">
                    ICCID <span style={{ color: "red" }}>*</span>
                  </label>
                  <PR.InputText
                    id="iccid"
                    name="iccid"
                    value={values.iccid}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter ICCID"
                    autoComplete="off"
                    className="w-full"
                    style={{ width: "100%" }}
                    disabled={fraudLoading}
                  />
                  <ErrorMessage
                    name="iccid"
                    component="div"
                    className="p-error text-sm mt-1"
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="block mb-1 font-bold text-sm">Note</label>
                <PR.InputTextarea
                  name="note"
                  value={values.note}
                  onChange={handleChange}
                  placeholder="Enter note (optional)"
                  autoResize
                  rows={2}
                  style={{ width: "100%" }}
                  disabled={fraudLoading}
                />
                <ErrorMessage
                  name="note"
                  component="div"
                  className="p-error text-sm mt-1"
                />
              </div>

              <div className="p-mt-2 text-right mt-3 flex justify-content-end align-items-center gap-2">
                <PR.Button
                  type="button"
                  label="Cancel"
                  className="p-button-secondary p-button-outlined"
                  style={{ height: "40px" }}
                  onClick={() => hideDialog("shippingStatus")}
                  disabled={fraudLoading}
                />
                <PR.Button
                  type="submit"
                  label="Update Status"
                  className="p-button save-button"
                  style={{ height: "40px" }}
                  loading={fraudLoading}
                  disabled={
                    fraudLoading || availableShippingStatusOptions.length === 0
                  }
                />
              </div>
            </Form>
          )}
        </Formik>
      </PR.Dialog>
    </div>
  );
};

export default OrderActionMenu;
